import { Photo } from "react-photo-album";
import type { GalleryItem } from "react-image-gallery";
import { Painting, Category, findCategory } from "./portfolio";

const carouselImages: Record<string, any> = 
  import.meta.glob(
    './images/gallery/*.{png,jpg,jpeg,svg,PNG,JPG,JPEG,SVG}', 
    { query: { as: 'metadata' }, eager: true }
  );

const portfolioImages: Record<string, any> = 
  import.meta.glob(
    './images/portfolio/**/*.{png,jpg,jpeg,svg,PNG,JPG,JPEG,SVG}', 
    { eager: true }
  );

const portfolio800: Record<string, any> = 
  import.meta.glob(
    './images/portfolio/**/*.{png,jpg,jpeg,svg,PNG,JPG,JPEG,SVG}', 
    { query: { w: 800, h: 800, fit: 'inside', as: 'metadata' }, eager: true }
  );

const portfolio400: Record<string, any> = 
  import.meta.glob(
    './images/portfolio/**/*.{png,jpg,jpeg,svg,PNG,JPG,JPEG,SVG}', 
    { query: { w: 400, h: 400, fit: 'inside', as: 'metadata' }, eager: true }
  );

export interface ImageData extends Omit<Photo, 'title'>, Painting {
  path: string;
  caption: string;
  large?: string;
}

// Category
export function getCategoryThumbnail(category: string, filename: string): string {
  const path = `./images/portfolio/${category.toLowerCase()}/${filename}`;
  const thumbnail = portfolio800[path];
  const original = portfolioImages[path];
  
  return thumbnail?.src || original?.src || original?.default || filename;
}

// Portfolio
export function getCategoryImages(title: string): ImageData[] {
  const images: ImageData[] = [];
  const category: Category | undefined = findCategory(title);
  const paintings = category?.paintings || [];

  paintings.forEach(({file, ...meta}) => {
    const path = `./images/portfolio/${title.toLowerCase()}/${file}`;
    const imageModule = portfolio400[path];

    if (!imageModule) {
      console.warn(`Missing image for ${path}`);
      return;
    }

    const paintingData = {
      src: imageModule.src || imageModule.default, // fallback to default if src not available
      width: imageModule.width || 400, // fallback width
      height: imageModule.height || 300, // fallback height
      path: path,
      caption: file,
      file: file,
      ...meta
    }

    images.push(paintingData);
  });

  return images.map(({ path, src, title, ...imageData }) => {
    const large = portfolioImages[path];
    
    return {
      path: path,
      src: src,
      large: large.default || src,
      title: title,
      alt: title,
      ...imageData
    };
  });
}

// Carousel
export const carouselImageData: GalleryItem[] = 
  Object.values(carouselImages).map((image) => {
    return {
      original: image.src,
      originalWidth: image.width ? String(image.width) : undefined,
      originalAlt: image.default,
    };
});
